import {
  CreditCard,
  CustomerAddresses,
  CustomerCountry,
  CustomerExistsResponse,
  CustomerExploreMoreCard,
  CustomerTitle,
  CustomerVoucher,
  MultiFasciaPreferences,
  PreferenceListItem,
} from '@as-react/common-types';
import type { QueryKey, UseQueryOptions } from '@tanstack/react-query';

import type { OptionalQueryOptions, QueryApiOptions } from '../../types/types';

import {
  getLanguages,
  getTitles,
  getGenders,
  getCountries,
  getPreferencesNewsletter,
  type CustomerGendersParams,
  type CustomerTitlesParams,
  type CustomerPreferencesNewsletterParams,
  CustomerEmailExistsParams,
  getEmailExists,
  getDeliveryAddresses,
  getProfile,
  getCardNumberExists,
  getPreferences,
  getMultiFasciaPreferences,
  getCardInfo,
  getVouchers,
} from './customer.api';

export const customerQueryKeys = {
  all: () => ['customers'] as QueryKey,
  languages: () => [...customerQueryKeys.all(), 'languages'] as QueryKey,
  titles: () => [...customerQueryKeys.all(), 'titles'] as QueryKey,
  genders: () => [...customerQueryKeys.all(), 'genders'] as QueryKey,
  countries: () => [...customerQueryKeys.all(), 'countries'] as QueryKey,
  preferencesNewsletter: () => [...customerQueryKeys.all(), 'preferencesNewsletter'] as QueryKey,
  emailExists: (params: CustomerEmailExistsParams) => [...customerQueryKeys.all(), 'exists', params] as QueryKey,
  getDeliveryAddresses: () => [...customerQueryKeys.all(), 'deliveryAddresses'] as QueryKey,
  profile: () => [...customerQueryKeys.all(), 'profile'] as QueryKey,
  cardNumberExists: (cardNumber: string) => [...customerQueryKeys.all(), 'cardNumberExists', cardNumber] as QueryKey,
  getPreferences: () => [...customerQueryKeys.all(), 'getPreferences'] as QueryKey,
  getAllPreferences: () => [...customerQueryKeys.all(), 'getAllPreferences'] as QueryKey,
  getCardInfo: () => [...customerQueryKeys.all(), 'getCardInfo'] as QueryKey,
  getVouchers: () => [...customerQueryKeys.all(), 'getVouchers'] as QueryKey,
};

export const customerQueries = (apiOptions: QueryApiOptions) => ({
  getLanguages: () => ({
    queryKey: customerQueryKeys.languages(),
    queryFn: () => getLanguages(apiOptions),
  }),
  getTitles: <TReturnData = { hasGenderField: boolean; options: Array<{ value: number; label: string }> }>(
    params: CustomerTitlesParams,
    options: OptionalQueryOptions<Array<CustomerTitle>, TReturnData> = {}
  ): UseQueryOptions<Array<CustomerTitle>, Error, TReturnData> => ({
    queryKey: customerQueryKeys.titles(),
    queryFn: () => getTitles(apiOptions, params),
    select: (data: Array<CustomerTitle>) =>
      ({
        options:
          data
            .sort((t1, t2) => (t1.sortOrder && t2.sortOrder ? t1.sortOrder - t2.sortOrder : 0))
            .map(title => ({ value: title.id, label: title.title })) || null,
        hasGenderField: Boolean(data.some(title => !title.idGender || title.idGender === '-1')),
      }) as TReturnData,
    ...options,
  }),
  getGenders: (params: CustomerGendersParams) => ({
    queryKey: customerQueryKeys.genders(),
    queryFn: () => getGenders(apiOptions, params),
  }),
  getCountries: <TReturnData = Array<CustomerCountry>>(
    options: OptionalQueryOptions<Array<CustomerCountry>, TReturnData> = {}
  ): UseQueryOptions<Array<CustomerCountry>, Error, TReturnData> => ({
    ...options,
    queryKey: customerQueryKeys.countries(),
    queryFn: () => getCountries(apiOptions),
  }),
  getPreferencesNewsletter: (
    params: CustomerPreferencesNewsletterParams,
    options: OptionalQueryOptions<Record<string, boolean>>
  ): UseQueryOptions<Record<string, boolean>, Error, Record<string, boolean>> => ({
    ...options,
    queryKey: customerQueryKeys.preferencesNewsletter(),
    queryFn: () => getPreferencesNewsletter(apiOptions, params),
  }),
  getEmailExists: (
    params: CustomerEmailExistsParams,
    options: OptionalQueryOptions<CustomerExistsResponse> = {}
  ): UseQueryOptions<CustomerExistsResponse, Error, CustomerExistsResponse> => ({
    ...options,
    queryKey: customerQueryKeys.emailExists(params),
    queryFn: () => getEmailExists(apiOptions, params),
  }),
  getDeliveryAddresses: <TReturnData = CustomerAddresses>(
    options: OptionalQueryOptions<CustomerAddresses, TReturnData> = {}
  ): UseQueryOptions<CustomerAddresses, Error, TReturnData> => ({
    ...options,
    queryKey: customerQueryKeys.getDeliveryAddresses(),
    queryFn: () => getDeliveryAddresses(apiOptions),
  }),
  getProfile: () => ({
    queryKey: customerQueryKeys.profile(),
    queryFn: () => getProfile(apiOptions),
  }),
  getCardNumberExists: (
    cardNumber: string,
    options: OptionalQueryOptions<CustomerExploreMoreCard> = {}
  ): UseQueryOptions<CustomerExploreMoreCard, Error, CustomerExploreMoreCard> => ({
    ...options,
    queryKey: customerQueryKeys.cardNumberExists(cardNumber),
    queryFn: () => getCardNumberExists(apiOptions, cardNumber),
  }),
  getPreferences: (
    options: OptionalQueryOptions<Array<PreferenceListItem>> = {}
  ): UseQueryOptions<Array<PreferenceListItem>, Error, Array<PreferenceListItem>> => ({
    ...options,
    queryKey: customerQueryKeys.getPreferences(),
    queryFn: () => getPreferences(apiOptions),
  }),
  getAllPreferences: (
    options: OptionalQueryOptions<MultiFasciaPreferences> = {}
  ): UseQueryOptions<MultiFasciaPreferences, Error, MultiFasciaPreferences> => ({
    ...options,
    queryKey: customerQueryKeys.getAllPreferences(),
    queryFn: () => getMultiFasciaPreferences(apiOptions),
  }),
  getCardInfo: (
    options: OptionalQueryOptions<Array<CreditCard>> = {}
  ): UseQueryOptions<Array<CreditCard>, Error, Array<CreditCard>> => ({
    ...options,
    queryKey: customerQueryKeys.getCardInfo(),
    queryFn: () => getCardInfo(apiOptions),
  }),
  getVouchers: (
    options: OptionalQueryOptions<Array<CustomerVoucher>> = {}
  ): UseQueryOptions<Array<CustomerVoucher>, Error, Array<CustomerVoucher>> => ({
    ...options,
    queryKey: customerQueryKeys.getVouchers(),
    queryFn: () => getVouchers(apiOptions),
  }),
});
